import { inject, Injectable } from '@angular/core';

import { AuthService } from '../../core/auth/auth.service';

/** What a folding card tells the service about itself while it is on the page. */
interface FoldedCard {
  /** Ids of elements inside the card that an in-page link may point at. */
  anchors(): readonly string[];
  open(): void;
}

/**
 * FLOW-402 (DOC-89-A §4, UI-DA-89-A-04) — the page remembers, for each person
 * and each application, which cards they left open, and opens a folded card
 * when a link points inside it.
 *
 * **A viewing aid, not a fact of the application.** What one person opens is
 * never forced on another and never reaches the record: it lives in this
 * browser's localStorage, keyed by the signed-in user and the application
 * (`scope`). Nothing stored, or storage unavailable, reads as "nothing
 * remembered" and every card takes its default — so the page renders the
 * same with none of this. Every read and write is wrapped: a private window
 * or blocked site data must not take the page down.
 *
 * 🔒 Folding never hides work: `reveal(id)` is how *What happens next* and the
 * Decision card reach a task inside a folded card — the card opens, then the
 * target scrolls into view.
 */
@Injectable({ providedIn: 'root' })
export class CardFoldService {
  private readonly auth = inject(AuthService);
  private readonly cards = new Set<FoldedCard>();

  private key(scope: string): string {
    const user = this.auth.currentUser()?.id ?? 'anonymous';
    return `flow.cards.${user}.${scope}`;
  }

  private read(scope: string): Record<string, boolean> {
    try {
      const raw = localStorage.getItem(this.key(scope));
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, boolean>) : {};
    } catch {
      return {};
    }
  }

  /** The remembered state of one card, or null when nothing is remembered. */
  isOpen(scope: string, card: string): boolean | null {
    const open = this.read(scope)[card];
    return typeof open === 'boolean' ? open : null;
  }

  remember(scope: string, card: string, open: boolean): void {
    try {
      const state = { ...this.read(scope), [card]: open };
      localStorage.setItem(this.key(scope), JSON.stringify(state));
    } catch {
      // Storage unavailable: the card still opens; it is just not remembered.
    }
  }

  register(card: FoldedCard): () => void {
    this.cards.add(card);
    return () => this.cards.delete(card);
  }

  /**
   * Open whichever card holds `id`, then scroll to it. A target outside any
   * folding card, or already rendered, scrolls at once.
   */
  reveal(id: string): void {
    const holder = Array.from(this.cards).find((c) => c.anchors().includes(id));
    if (holder) holder.open();
    const scroll = () => {
      const target = document.getElementById(id);
      if (!target) return;
      let parent: HTMLElement | null = target;
      while (parent) {
        if (parent instanceof HTMLDetailsElement) parent.open = true;
        parent = parent.parentElement;
      }
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    if (holder) setTimeout(scroll, 0);
    else scroll();
  }
}
